import React from 'react'
import { getDNSTierNews } from '../../api/otc/company/news'
import { getSECFilingsByTier, getFilingsExternal } from '../../api/otc/company/filings'
import { getFinancialReportsByTier } from '../../api/otc/company/financials'
import { getDNSNewsUrl } from '../../utils/news-urls'
import { template, fetch, expand, sort } from '../../api/helper'
import api from '../../api/api'
import getConfig from '../../config'
import styles from './MANewsPage.module.scss'

import ExternalLink from '../../components/ExternalLink'
import HeadTitle from '../../components/HeadTitle'
import Flexgrid from '../../components/Flexgrid'
import Outline from '../../components/Outline'
import Table from '../../components/Table'
import More from '../../components/More'
import TableFooter from '../../components/TableFooter'
import DisplayResults from '../../components/DisplayResults'
import Loading from '../../components/Loading'
import PageTitle from '../../components/PageTitle'
import Sticky from '../../components/Sticky'
import JumpLinkNav from '../../components/JumpLinkNav'

const hostname = window.location.origin
const { OTC_HOSTNAME_URL } = getConfig()

const jumpLinks = [
  {
    label: 'News Releases',
    hash: 'news-releases'
  }, {
    label: 'Financial Reports',
    hash: 'financial-reports'
  }, {
    label: 'SEC Filings',
    hash: 'sec-filings'
  }, {
    label: 'Non-U.S. Disclosure',
    hash: 'non-us-disclosure'
  }
]

/** Headers for Table */
const columns = {
  news: [
    {
      name: 'releaseDate',
      header: 'Release Date',
      type: 'date',
      sortingEnabled: true
    },
    {
      name: 'symbol',
      header: 'Symbol',
      type: 'symbol'
    },
    {
      name: 'title',
      header: 'Title',
      component: (item) => <div>
        <ExternalLink className={styles.newsTitle} to={getDNSNewsUrl(item.data.symbol, item.data)}>{item.data.title}</ExternalLink>
        <abbr className={styles.newsSubtitle}>
          {item.data.sourceDesc}
        </abbr>
      </div>
    }
  ],
  secFilings: [
    {
      name: 'receivedDate',
      header: 'Received Date',
      type: 'date',
      sortingEnabled: true,
      color: (item) => item.valid === false && '#bbb'
    },
    {
      name: 'symbol',
      header: 'Symbol',
      type: 'symbol',
      color: (item) => item.valid === false && '#bbb'
    },
    {
      name: 'companyName',
      header: 'Company Name',
      hideOnMobile: true,
      color: (item) => item.valid === false && '#bbb'
    },
    {
      name: 'formType',
      header: 'FormType',
      target: 'blank',
      linkTo: (item) => `${hostname}/otc-content/filing/html?id=${item.id}&guid=${api.getGUID(item.guid)}`, // set back to item.guid once OTCM-429 is ready
      color: (item) => item.valid === false && '#bbb'
    },
    {
      name: 'periodEndDate',
      header: 'Period End Date',
      type: 'date',
      hideOnTablet: true,
      hideOnMobile: true,
      color: (item) => item.valid === false && '#bbb'
    }
  ],
  financialReports: [
    {
      name: 'releaseDate',
      header: 'Release Date',
      type: 'date',
      sortingEnabled: true
    },
    {
      name: 'symbol',
      header: 'Symbol',
      type: 'symbol'
    },
    {
      name: 'reportType',
      header: 'Report Type',
      hideOnMobile: true
    },
    {
      name: 'title',
      header: 'Title',
      linkTo: (item) => {
        return api.link({
          otcAPI: true,
          url: `/backend-content/company/financial-report/${item.id}/content`
        })
      }
    },
    {
      name: 'periodDate',
      header: 'Period End Date',
      type: 'date',
      sortingEnabled: true,
      hideOnTablet: true,
      hideOnMobile: true
    }
  ],
  nonUsDisclosure: [
    {
      name: 'releaseDate',
      header: 'Publish Date',
      type: 'date'
    },
    {
      name: 'symbol',
      header: 'Symbol',
      type: 'symbol',
      componentOptions: {
        pageName: (item) => `/stock/${item.symbol}/disclosure`
      }
    },
    {
      name: 'title',
      header: 'Document',
      linkTo: (item) => {
        if (item.id) {
          return `${OTC_HOSTNAME_URL}/filing/${item.id}/content`
        } else {
          return null
        }
      }
    },
    {
      name: 'periodDate',
      header: 'Period End Date',
      type: 'date',
      hideOnMobile: true
    },
    {
      name: 'source',
      header: 'Source',
      hideOnMobile: true
    }
  ]
}

const baseTiers = [
  { title: 'OTCQX', value: 'QX' },
  { title: 'OTCQB', value: 'DQ' },
  { title: 'OTCID', value: 'OM' },
  { title: 'Pink Limited', value: 'PS' }
]

const makeTierList = (allMarketsValue) => [
  { title: 'All Markets', value: allMarketsValue },
  ...baseTiers
]

const tierListNews = makeTierList('QX,DQ,OM,PS')
const tierListReports = makeTierList('ALL')
const tierListFilings = makeTierList('ALL')
const tierListNonUsDisclosure = makeTierList('ALL')


const TABLE_CONFIG = {
  news: {
    func: getDNSTierNews,
    tierParam: 'tierGroupIds',
    defaultTier: 'QX,DQ,OM,PS'
  },
  secFilings: {
    func: getSECFilingsByTier,
    tierParam: 'tierGroup',
    additionalParams: { includeListed: false },
    defaultTier: 'ALL'
  },
  financialReports: {
    func: getFinancialReportsByTier,
    tierParam: 'tierGroup',
    additionalParams: { onlyRecent: true },
    defaultTier: 'ALL'
  },
    nonUsDisclosure: {
    func: getFilingsExternal,
    tierParam: 'tierGroups',
    additionalParams: {
      statusId: 'ACTIVE',
      reportSources: ['NSM', 'SEDAR'],
      sort: 'releaseDate,desc'
    },
    defaultTier: 'ALL',
    useContentArray: true,
    useSizeParam: true
  }
}

class MANewsPage extends React.PureComponent {
  constructor (props, context) {
    super(props, context)

    const tableState = Object.keys(TABLE_CONFIG).reduce((acc, tableName) => {
      // For APIs that use 'content' instead of 'records'
      acc[tableName] = TABLE_CONFIG[tableName].useContentArray
        ? template('content')
        : template('records')
      return acc
    }, {})

    const tierSelection = Object.keys(TABLE_CONFIG).reduce((acc, tableName) => {
      acc[tableName] = TABLE_CONFIG[tableName].defaultTier
      return acc
    }, {})

    this.state = {
      ...tableState,
      tierSelection
    }
  }

  /**
   * Get table data
   */
  componentDidMount() {
    Object.keys(TABLE_CONFIG).forEach(tableName => {
      this.loadList(tableName)()
    })

    // If anchor is supplied in hash, scroll into view
    const hash = window.location.hash
    if (hash && hash.length > 1) {
      const el = document.getElementById(hash.substr(1))
      if (el) {
        const elOffSet = el.offsetTop
        window.scrollTo({ top: elOffSet - 100 })
      }
    }
  }

  /**
   * Load the data
   */
  loadList = (tableName) => {
    return () => {
      if (!tableName) {
        console.error('No table name supplied.')
        return
      }

      const tableConfig = TABLE_CONFIG[tableName]

      if (!tableConfig) {
        console.error(`Invalid table name: ${tableName}`)
        return
      }
      if (!this.state.tierSelection[tableName]) {
        console.warn(`No tier selection found for table: ${tableName}`)
      }

      const params = {
        [tableConfig.tierParam]: this.state.tierSelection[tableName],
        page: 1,
        pageSize: 10,
        ...tableConfig.additionalParams
      }

      // For APIs that use 'size' parameter instead of 'pageSize'
      if (tableConfig.useSizeParam) {
        params.size = params.pageSize
      }

      fetch(this, tableConfig.func, tableName, params)
    }
  }

  onTierSelect = (tableName) => {
    return (tierName) => {
      if (!tableName) {
        console.error('No table name supplied.')
        return
      }
      if (!TABLE_CONFIG[tableName]) {
        console.error(`Invalid table name: ${tableName}`)
        return
      }
      if (!tierName) {
        console.error('No tier selection supplied.')
        return
      }
      this.setState(state => ({
        tierSelection: {
          ...state.tierSelection,
          [tableName]: tierName
        }
      }), () => {
        this.loadList(tableName)()
      })
    }
  }

  render() {
    const layouts = {
      sm: [
        {
          type: 'flex',
          column: true,
          subtree: ['info', 'news', 'financial-reports', 'sec-filings', 'non-us-disclosure']
        }
      ],
      md: [
        {
          type: 'flex',
          column: true,
          subtree: ['info', 'news', 'financial-reports', 'sec-filings', 'non-us-disclosure', 'footer-note']
        }
      ],
      lg: [
        {
          type: 'flex',
          subtree: [
            {
              type: 'side',
              subtree: ['info', 'links']
            },
            {
              type: 'main',
              subtree: ['news', 'financial-reports', 'sec-filings', 'non-us-disclosure', 'footer-note']
            }
          ]
        }
      ]
    }

    return (
      <div className={styles.MANewsPage}>
        <HeadTitle title='Get OTC Disclosure And News Services' />
        <PageTitle>Company News &amp; Financial Reports</PageTitle>
        <Flexgrid layouts={layouts}>
          <div key='info' className={styles.info}>
            <p>
              Access new releases and financial reports on
              companies that use the OTC Disclosure &amp;
              News Service to distribute their information.
            </p>
          </div>
          <div key='news' id='news-releases' className={styles.table}>
            <Outline mode='heading' hideFilterToggle filters={[{
              type: 'select',
              required: true,
              options: tierListNews,
              defaultValue: tierListNews[0].value,
              onSelect: this.onTierSelect('news')
            }]}>
              News Releases
            </Outline>

            <Loading
              type='table'
              loaded={this.state.news.loaded}
              reloading={this.state.news.reloading}
              error={this.state.news.error}
              data={this.state.news.data.records}>
              <Table
                columns={columns.news}
                data={this.state.news.data.records}
                sortColumn={this.state.news.sortOn}
                sortDirection={this.state.news.sortDir}
                onSorting={sort(this, getDNSTierNews, 'news')}
                showHeader
              />
              <TableFooter>
                <More onClick={expand(this, getDNSTierNews, 'news')}
                  disabled={this.state.news.expandEmpty} />
                {this.state.news.data.records && this.state.news.data.totalRecords &&
                  <DisplayResults show={this.state.news.data.records.length} total={this.state.news.data.totalRecords} text={'Releases'} />
                }
              </TableFooter>
            </Loading>
          </div>
          <div key='financial-reports' id='financial-reports' className={styles.table}>
            <Outline mode='heading' hideFilterToggle filters={[{
              type: 'select',
              required: true,
              options: tierListReports,
              defaultValue: tierListReports[0].value,
              onSelect: this.onTierSelect('financialReports')
            }]}>
              Financial Reports
            </Outline>
            <Loading
              type='table'
              loaded={this.state.financialReports.loaded}
              reloading={this.state.financialReports.reloading}
              error={this.state.financialReports.error}
              data={this.state.financialReports.data.records}>
              <Table
                columns={columns.financialReports}
                data={this.state.financialReports.data.records}
                sortColumn={this.state.financialReports.sortOn}
                sortDirection={this.state.financialReports.sortDir}
                onSorting={sort(this, getFinancialReportsByTier, 'financialReports')}
                showHeader
              />
              <TableFooter>
                <More onClick={expand(this, getFinancialReportsByTier, 'financialReports')}
                  disabled={this.state.financialReports.expandEmpty} />
                {this.state.financialReports.data.records && this.state.financialReports.data.totalRecords &&
                  <DisplayResults show={this.state.financialReports.data.records.length} total={this.state.financialReports.data.totalRecords} text={'Reports'} />
                }
              </TableFooter>
            </Loading>
          </div>
          <div key='sec-filings' id='sec-filings' className={styles.table}>
            <Outline mode='heading' hideFilterToggle filters={[{
              type: 'select',
              required: true,
              options: tierListFilings,
              defaultValue: tierListFilings[0].value,
              onSelect: this.onTierSelect('secFilings')
            }]}>
              SEC Filings
            </Outline>
            <Loading
              type='table'
              loaded={this.state.secFilings.loaded}
              reloading={this.state.secFilings.reloading}
              error={this.state.secFilings.error}
              data={this.state.secFilings.data.records}>
              <Table
                columns={columns.secFilings}
                data={this.state.secFilings.data.records}
                sortColumn={this.state.secFilings.sortOn}
                sortDirection={this.state.secFilings.sortDir}
                onSorting={sort(this, getSECFilingsByTier, 'secFilings')}
                showHeader
              />
              <TableFooter>
                <More onClick={expand(this, getSECFilingsByTier, 'secFilings')}
                  disabled={this.state.secFilings.expandEmpty} />
                {this.state.secFilings.data.records && this.state.secFilings.data.totalRecords &&
                  <DisplayResults show={this.state.secFilings.data.records.length} total={this.state.secFilings.data.totalRecords} text={'Filings'} />
                }
              </TableFooter>
            </Loading>
          </div>
          <div key='non-us-disclosure' id='non-us-disclosure' className={styles.table}>
            <Outline mode='heading' hideFilterToggle filters={[{
              type: 'select',
              required: true,
              options: tierListNonUsDisclosure,
              defaultValue: tierListNonUsDisclosure[0].value,
              onSelect: this.onTierSelect('nonUsDisclosure')
            }]}>
              Non-U.S. Disclosure
            </Outline>
            <Loading
              type='table'
              loaded={this.state.nonUsDisclosure.loaded}
              reloading={this.state.nonUsDisclosure.reloading}
              error={this.state.nonUsDisclosure.error}
              data={this.state.nonUsDisclosure.data.content || []}>
              <Table
                columns={columns['nonUsDisclosure']}
                data={this.state.nonUsDisclosure.data.content || []}
                showHeader
              />
              <TableFooter>
                <More onClick={expand(this, getFilingsExternal, 'nonUsDisclosure')}
                  disabled={this.state.nonUsDisclosure.expandEmpty} />
                {this.state.nonUsDisclosure.data.content && this.state.nonUsDisclosure.data.total &&
                  <DisplayResults show={this.state.nonUsDisclosure.data.content.length} total={this.state.nonUsDisclosure.data.total} text={'Non-U.S. Disclosure Filings'} />
                }
              </TableFooter>
            </Loading>
          </div>
          <Sticky key='links'>
            <JumpLinkNav links={jumpLinks} />
          </Sticky>
        </Flexgrid>
      </div>
    )
  }
}

export default MANewsPage
